import { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SetupPortalPage from '../../components/setup/SetupPortalPage';
import BackToAgentSetupLink from '../../components/setup/BackToAgentSetupLink';
import api from '../../API.js';
import { monitorRagJobStatus } from '../../utils/ragUploadHelper';
import { useGirderUpdates } from '../../contexts/GirderUpdatesContext';

function DocumentsManagement() {
    const [searchParams] = useSearchParams();
    const initialCollection = searchParams.get('collection') || '';
    const girderUpdatesContext = useGirderUpdates();
    
    const [collections, setCollections] = useState([]);
    const [selectedCollection, setSelectedCollection] = useState(initialCollection);
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState('');
    const [uploadSuccess, setUploadSuccess] = useState('');
    const [uploadProgress, setUploadProgress] = useState(0);
    const [pastedText, setPastedText] = useState('');
    const [selectedDocument, setSelectedDocument] = useState(null);
    const fileInputRef = useRef(null);
    const jobMonitorCleanupRef = useRef(null);

    useEffect(() => {
        fetchCollections();
    }, []);

    useEffect(() => {
        if (collections.length > 0) {
            fetchDocuments();
        }
    }, [selectedCollection, collections]);

    // Cleanup job monitor on unmount
    useEffect(() => {
        return () => {
            if (jobMonitorCleanupRef.current) {
                jobMonitorCleanupRef.current();
                jobMonitorCleanupRef.current = null;
            }
        };
    }, []);

    const fetchCollections = async () => {
        try {
            const response = await api.get('agent/rag/collections');
            if (response.status === 200) {
                const collectionNames = (response.data.collections || []).map(c => c.collectionName);
                setCollections(collectionNames);
                if (initialCollection && collectionNames.includes(initialCollection)) {
                    setSelectedCollection(initialCollection);
                } else if (collectionNames.length > 0 && !selectedCollection) {
                    setSelectedCollection(collectionNames[0]);
                }
            }
        } catch (err) {
            console.error('Error fetching collections:', err);
        }
    };

    const fetchDocuments = async () => {
        if (!selectedCollection || selectedCollection === 'all') {
            // Fetch from all collections
            setDocuments([]);
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError('');
            const response = await api.get(`agent/rag/collections/${selectedCollection}/documents`);
            if (response.status !== 200) {
                throw new Error(`Failed to fetch documents: ${response.status}`);
            }
            setDocuments(response.data.documents || []);
        } catch (err) {
            console.error('Error fetching documents:', err);
            setError('Failed to load documents: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!selectedCollection || selectedCollection === 'all') {
            setUploadError('Please select a collection first.');
            return;
        }

        setUploading(true);
        setUploadError('');
        setUploadSuccess('');
        setUploadProgress(0);

        try {
            const formData = new FormData();
            formData.append('file', file);

            const response = await api.post(`agent/rag/collections/${selectedCollection}/upload`, {
                body: formData,
            });

            if (response.status === 200 && response.data.jobId) {
                const jobId = response.data.jobId;
                const filename = response.data.filename || file.name;
                
                // Clean up any existing monitor
                if (jobMonitorCleanupRef.current) {
                    jobMonitorCleanupRef.current();
                }
                
                // Start monitoring job status (uses WebSocket if available, falls back to polling)
                jobMonitorCleanupRef.current = monitorRagJobStatus(
                    jobId,
                    {
                        onProgress: (percent) => setUploadProgress(percent),
                        onSuccess: ({ chunks }) => {
                            setUploadSuccess(`Document "${filename}" uploaded successfully! ${chunks} chunks created.`);
                            if (fileInputRef.current) {
                                fileInputRef.current.value = '';
                            }
                            setUploadProgress(100);
                            fetchDocuments(); // Refresh document list
                        },
                        onError: (errorMsg) => {
                            setUploadError('Upload failed: ' + errorMsg);
                            setUploadProgress(0);
                        },
                        onComplete: () => {
                            setUploading(false);
                            jobMonitorCleanupRef.current = null;
                        },
                    },
                    {
                        girderUpdatesContext,
                        jobStatusEndpoint: 'agent/rag/jobs',
                        useWebSocket: true,
                    }
                );
            } else {
                throw new Error(response.data.error || 'Upload failed');
            }
        } catch (err) {
            console.error('Error uploading file:', err);
            setUploadError('Failed to upload document. ' + (err.message || err));
            setUploading(false);
            setUploadProgress(0);
        }
    };

    const handleTextUpload = async () => {
        if (!pastedText.trim()) {
            setUploadError('Please paste some text first.');
            return;
        }

        if (!selectedCollection || selectedCollection === 'all') {
            setUploadError('Please select a collection first.');
            return;
        }

        setUploading(true);
        setUploadError('');
        setUploadSuccess('');
        setUploadProgress(0);

        try {
            const formData = new FormData();
            formData.append('text', pastedText);
            formData.append('filename', `pasted_text_${Date.now()}.txt`);

            const response = await api.post(`agent/rag/collections/${selectedCollection}/upload`, {
                body: formData,
            });

            if (response.status === 200 && response.data.jobId) {
                const jobId = response.data.jobId;
                const filename = response.data.filename || `pasted_text_${Date.now()}.txt`;
                
                // Clean up any existing monitor
                if (jobMonitorCleanupRef.current) {
                    jobMonitorCleanupRef.current();
                }
                
                // Start monitoring job status (uses WebSocket if available, falls back to polling)
                jobMonitorCleanupRef.current = monitorRagJobStatus(
                    jobId,
                    {
                        onProgress: (percent) => setUploadProgress(percent),
                        onSuccess: ({ chunks }) => {
                            setUploadSuccess(`Text uploaded successfully! ${chunks} chunks created.`);
                            setPastedText('');
                            setUploadProgress(100);
                            fetchDocuments(); // Refresh document list
                        },
                        onError: (errorMsg) => {
                            setUploadError('Upload failed: ' + errorMsg);
                            setUploadProgress(0);
                        },
                        onComplete: () => {
                            setUploading(false);
                            jobMonitorCleanupRef.current = null;
                        },
                    },
                    {
                        girderUpdatesContext,
                        jobStatusEndpoint: 'agent/rag/jobs',
                        useWebSocket: true,
                    }
                );
            } else {
                throw new Error(response.data.error || 'Upload failed');
            }
        } catch (err) {
            console.error('Error uploading text:', err);
            setUploadError('Failed to upload text. ' + (err.message || err));
            setUploading(false);
            setUploadProgress(0);
        }
    };

    const handleDeleteDocument = async (documentId) => {
        if (!selectedCollection || selectedCollection === 'all') {
            setError('Cannot delete: no collection selected');
            return;
        }

        const document = documents.find(d => d.documentId === documentId);
        const chunkCount = document?.chunks?.length || 0;

        if (!window.confirm(`Are you sure you want to delete this document?\n\nThis will delete ${chunkCount} chunk(s).\n\nThis action cannot be undone.`)) {
            return;
        }

        try {
            setError('');
            const response = await api.delete(`agent/rag/collections/${selectedCollection}/documents/${documentId}`);

            if (response.status !== 200) {
                const errorData = typeof response.data === 'string' ? {} : response.data;
                throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
            }

            await fetchDocuments();
        } catch (err) {
            console.error('Error deleting document:', err);
            setError('Failed to delete document: ' + err.message);
        }
    };

    return (
        <SetupPortalPage>
            <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
                <div style={{ marginBottom: '20px' }}>
                    <BackToAgentSetupLink />
                </div>
                
                <h1>Documents Management</h1>
                
                {error && (
                    <div style={{ color: 'red', marginBottom: '20px', padding: '10px', backgroundColor: '#ffe6e6', borderRadius: '5px' }}>
                        {error}
                    </div>
                )}

                {/* Collection Filter */}
                <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '5px' }}>
                    <label htmlFor="collection-select" style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
                        Filter by Collection:
                    </label>
                    <select
                        id="collection-select"
                        value={selectedCollection}
                        onChange={(e) => setSelectedCollection(e.target.value)}
                        style={{ padding: '8px 15px', fontSize: '14px', minWidth: '200px' }}
                    >
                        <option value="all">All Collections</option>
                        {collections.map(coll => (
                            <option key={coll} value={coll}>{coll}</option>
                        ))}
                    </select>
                    {selectedCollection && selectedCollection !== 'all' && (
                        <div style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>
                            Showing documents from: <strong>{selectedCollection}</strong>
                        </div>
                    )}
                </div>

                {/* Upload Section */}
                {selectedCollection && selectedCollection !== 'all' && (
                    <div style={{ marginBottom: '30px', padding: '20px', backgroundColor: '#f9f9f9', borderRadius: '5px' }}>
                        <h2>Upload Documents</h2>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                            <div>
                                <label htmlFor="file-upload" style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
                                    Upload PDF or Text File
                                </label>
                                <input
                                    ref={fileInputRef}
                                    id="file-upload"
                                    type="file"
                                    accept=".pdf,.txt,.md,.markdown"
                                    onChange={handleFileUpload}
                                    disabled={uploading}
                                    style={{ width: '100%', padding: '8px', fontSize: '14px' }}
                                />
                            </div>
                            <div>
                                <label htmlFor="pasted-text" style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
                                    Or Paste Text:
                                </label>
                                <textarea
                                    id="pasted-text"
                                    value={pastedText}
                                    onChange={(e) => setPastedText(e.target.value)}
                                    disabled={uploading}
                                    placeholder="Paste text content here..."
                                    rows={4}
                                    style={{ width: '100%', padding: '8px', fontSize: '14px', marginBottom: '10px' }}
                                />
                                <button
                                    type="button"
                                    onClick={handleTextUpload}
                                    disabled={uploading || !pastedText.trim()}
                                    style={{
                                        padding: '8px 16px',
                                        fontSize: '14px',
                                        backgroundColor: uploading ? '#6c757d' : '#28a745',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '5px',
                                        cursor: uploading ? 'not-allowed' : 'pointer',
                                    }}
                                >
                                    {uploading ? 'Uploading...' : 'Upload Text'}
                                </button>
                                {uploading && (
                                    <div style={{ marginTop: '10px' }}>
                                        <div style={{ 
                                            width: '100%', 
                                            height: '20px', 
                                            backgroundColor: '#e0e0e0', 
                                            borderRadius: '10px',
                                            overflow: 'hidden'
                                        }}>
                                            <div style={{
                                                width: `${Math.max(uploadProgress, 0)}%`,
                                                height: '100%',
                                                backgroundColor: '#28a745',
                                                transition: 'width 0.3s ease'
                                            }}></div>
                                        </div>
                                        <div style={{ marginTop: '5px', fontSize: '12px', color: '#666' }}>
                                            {Math.max(Math.round(uploadProgress), 0)}% complete
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        {uploadError && (
                            <div style={{ color: 'red', marginTop: '10px', padding: '10px', backgroundColor: '#ffe6e6', borderRadius: '5px' }}>
                                {uploadError}
                            </div>
                        )}
                        {uploadSuccess && (
                            <div style={{ color: 'green', marginTop: '10px', padding: '10px', backgroundColor: '#e6ffe6', borderRadius: '5px' }}>
                                {uploadSuccess}
                            </div>
                        )}
                    </div>
                )}

                {/* Documents List */}
                <div>
                    <h2>Documents {selectedCollection && selectedCollection !== 'all' ? `(${documents.length})` : ''}</h2>
                    {loading ? (
                        <div>Loading documents...</div>
                    ) : selectedCollection === 'all' ? (
                        <div style={{ padding: '20px', backgroundColor: '#fff3cd', borderRadius: '5px', border: '1px solid #ffc107' }}>
                            <p>Please select a collection from the dropdown above to view documents.</p>
                        </div>
                    ) : documents.length === 0 ? (
                        <div>No documents found in this collection. Upload documents to get started.</div>
                    ) : (
                        <div style={{ display: 'grid', gap: '15px' }}>
                            {documents.map(doc => (
                                <div
                                    key={doc.documentId}
                                    style={{
                                        padding: '20px',
                                        backgroundColor: '#fff',
                                        border: '1px solid #ddd',
                                        borderRadius: '5px',
                                    }}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '10px' }}>
                                        <div>
                                            <h3 style={{ margin: '0 0 5px 0' }}>{doc.title}</h3>
                                            <div style={{ color: '#666', fontSize: '14px' }}>ID: {doc.documentId}</div>
                                            {doc.uploadedAt && (
                                                <div style={{ color: '#666', fontSize: '14px', marginTop: '5px' }}>
                                                    Uploaded: {new Date(doc.uploadedAt).toLocaleString()}
                                                </div>
                                            )}
                                        </div>
                                        <div style={{ display: 'flex', gap: '10px' }}>
                                            <button
                                                onClick={() => setSelectedDocument(selectedDocument === doc.documentId ? null : doc.documentId)}
                                                style={{
                                                    padding: '5px 15px',
                                                    fontSize: '14px',
                                                    backgroundColor: '#007bff',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '3px',
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                {selectedDocument === doc.documentId ? 'Hide Details' : 'View Details'}
                                            </button>
                                            <button
                                                onClick={() => handleDeleteDocument(doc.documentId)}
                                                style={{
                                                    padding: '5px 15px',
                                                    fontSize: '14px',
                                                    backgroundColor: '#dc3545',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '3px',
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                    <div style={{ marginTop: '15px', fontSize: '14px' }}>
                                        <div><strong>Chunks:</strong> {doc.chunks?.length || 0}</div>
                                        <div style={{ marginTop: '5px' }}><strong>Source Type:</strong> {doc.sourceType || 'unknown'}</div>
                                        {doc.collectionName && (
                                            <div style={{ marginTop: '5px' }}><strong>Collection:</strong> {doc.collectionName}</div>
                                        )}
                                    </div>
                                    {selectedDocument === doc.documentId && (
                                        <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '5px' }}>
                                            <h4>Document Details</h4>
                                            <div style={{ fontSize: '12px', fontFamily: 'monospace', marginTop: '10px' }}>
                                                <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                    {JSON.stringify({
                                                        documentId: doc.documentId,
                                                        title: doc.title,
                                                        sourceType: doc.sourceType,
                                                        uploadedAt: doc.uploadedAt,
                                                        chunkCount: doc.chunks?.length || 0,
                                                        collectionName: doc.collectionName,
                                                    }, null, 2)}
                                                </pre>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </SetupPortalPage>
    );
}

export default DocumentsManagement;
